import { useCallback, useEffect, useState } from 'react';
import { FilesetResolver, HandLandmarker } from '@mediapipe/tasks-vision';

// Everything is served from our own origin (scripts/copy-mediapipe.mjs), so
// the lesson does not depend on a CDN being reachable from the classroom.
const WASM_BASE = '/mediapipe/wasm';
const MODEL = '/mediapipe/hand_landmarker.task';

// One landmarker for the page: the camera slide appears twice in the deck,
// and loading the model again on the second visit would cost seconds.
// Resolves to { landmarker, delegate }.
let shared = null;

function create(delegate) {
  return FilesetResolver.forVisionTasks(WASM_BASE)
    .then((vision) =>
      HandLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: MODEL, delegate },
        runningMode: 'VIDEO',
        numHands: 2,
      })
    )
    .then((landmarker) => ({ landmarker, delegate }));
}

function loadLandmarker() {
  if (!shared) {
    shared = create('GPU').catch(() => create('CPU'));
    shared.catch(() => {
      shared = null; // let a later visit try again
    });
  }
  return shared;
}

/**
 * The GPU landmarker can load and then fail on every frame (a lost or
 * missing WebGL context). Swap it for a CPU one; the caller reloads.
 */
function demoteToCpu() {
  shared = create('CPU');
  shared.catch(() => {
    shared = null;
  });
  return shared;
}

/**
 * Start loading the model now, so that it is ready by the time the camera
 * slide comes up — on a classroom network the files take seconds.
 */
export function preloadHandLandmarker() {
  loadLandmarker().catch(() => {});
}

/**
 * The hand landmarker, once it is ready. `status` is 'loading', 'ready' or
 * 'failed'; it fails if the model has not loaded within `timeoutMs`, so the
 * slide can switch to its demo mode instead of waiting in front of a class.
 */
export function useHandLandmarker({ enabled, timeoutMs = 8000 }) {
  const [state, setState] = useState({ status: enabled ? 'loading' : 'failed', landmarker: null, delegate: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let done = false;
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        setState({ status: 'failed', landmarker: null, delegate: null });
      }
    }, timeoutMs);
    (attempt > 0 ? demoteToCpu() : loadLandmarker()).then(
      ({ landmarker, delegate }) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        setState({ status: 'ready', landmarker, delegate });
      },
      () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        setState({ status: 'failed', landmarker: null, delegate: null });
      }
    );
    return () => {
      done = true;
      clearTimeout(timer);
    };
  }, [enabled, timeoutMs, attempt]);

  // Called when a GPU landmarker breaks mid-stream: try once on the CPU.
  const demote = useCallback(() => {
    setState({ status: 'loading', landmarker: null, delegate: null });
    setAttempt((n) => n + 1);
  }, []);

  return { ...state, demote };
}
